import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, RefreshCw, ExternalLink, AlertTriangle, CheckCircle2, Search } from 'lucide-react';

const STATUS_BADGE = {
  pass: { label: 'Eligible', className: 'bg-green-100 text-green-800 border-green-200' },
  warning: { label: 'Eligible · improvements', className: 'bg-amber-100 text-amber-900 border-amber-200' },
  error: { label: 'Not eligible', className: 'bg-red-100 text-red-800 border-red-200' },
};

export const RichResultsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/seo/rich-results');
      setData(response.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to run the audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-16 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const posts = data?.posts || [];

  const cards = [
    { key: 'total', label: 'Published posts' },
    { key: 'passing', label: 'Clean' },
    { key: 'with_warnings', label: 'Improvements' },
    { key: 'with_errors', label: 'Blocking issues' },
    { key: 'article_eligible', label: 'Article eligible' },
    { key: 'breadcrumb_eligible', label: 'Breadcrumbs eligible' },
    { key: 'faq_eligible', label: 'FAQ markup' },
  ];

  return (
    <div className="space-y-6" data-testid="rich-results-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map((card) => (
          <Card key={card.key} className={card.key === 'with_errors' && summary[card.key] ? 'border-red-300' : ''}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold" data-testid={`rich-summary-${card.key}`}>
                {summary[card.key] ?? 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Rich Results Audit
            </CardTitle>
            <CardDescription>
              Every published post checked against Google's Article, Breadcrumb and FAQ requirements.
              Sorted by views, so your top posts are first.
            </CardDescription>
          </div>
          <Button onClick={load} variant="outline" data-testid="rich-results-refresh-btn">
            <RefreshCw className="mr-2 h-4 w-4" />
            Re-run audit
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="rich-results-error">
              {error}
            </div>
          )}

          {(data?.notes || []).map((note) => (
            <p key={note} className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-3 mb-2">
              {note}
            </p>
          ))}

          <div className="divide-y mt-4" data-testid="rich-results-rows">
            {posts.map((post) => {
              const badge = STATUS_BADGE[post.status];
              const isOpen = expanded === post.slug;
              return (
                <div key={post.slug} className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{post.title}</span>
                        <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                        {post.eligible_types.map((type) => (
                          <Badge key={type} variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                            {type}
                          </Badge>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {post.canonical} · {post.word_count} words
                        {post.faq_count ? ` · ${post.faq_count} FAQs` : ''}
                        {post.has_image ? '' : ' · no image'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(post.errors.length > 0 || post.warnings.length > 0) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpanded(isOpen ? null : post.slug)}
                          data-testid={`rich-results-details-${post.slug}`}
                        >
                          {post.errors.length + post.warnings.length} note(s)
                        </Button>
                      )}
                      <a
                        href={post.rich_results_test_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`rich-results-google-link-${post.slug}`}
                      >
                        <Button variant="outline" size="sm">
                          Test in Google <ExternalLink className="ml-2 h-3 w-3" />
                        </Button>
                      </a>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-3 space-y-2 pl-1">
                      {post.errors.map((issue) => (
                        <p key={issue} className="text-sm text-red-700 flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {issue}
                        </p>
                      ))}
                      {post.warnings.map((issue) => (
                        <p key={issue} className="text-sm text-amber-800 flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {issue}
                        </p>
                      ))}
                      <a
                        href={post.schema_validator_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-700 underline"
                      >
                        Open in schema.org validator
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
            {posts.length === 0 && (
              <p className="py-10 text-center text-gray-500 flex items-center justify-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> No published posts yet.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RichResultsTab;
